import { drillStep, niceMax, type EChartDataset } from "echart-table-library";
import { applyChartSort } from "@/widgets/common/chartSort";
import type { ChartSort } from "@/shared/types";
import type {
  ApiGranularity,
  OverviewGranularity,
  OverviewKpi,
  OverviewKpiTotals,
  OverviewSelection,
  PrintOverviewPoint,
  PrintOverviewResponseRow,
} from "./types";

/**
 * Pure transformations (convention section 24): input → function → output.
 * No Redux, no API calls — the thunk feeds rows in, views read the result.
 */

/** Granularity → the granularity the API actually serves (`week` → day). */
export const API_GRANULARITY: Record<OverviewGranularity, ApiGranularity> = {
  day: "day",
  week: "day",
  month: "month",
  year: "year",
};

/** Drill chain, coarse → fine (drill up = towards `year`, down = `day`). */
const DRILL_ORDER: OverviewGranularity[] = ["year", "month", "week", "day"];

// The chain arithmetic is the LIBRARY helper (`drillStep` — the pure-logic
// companion of `DrillControls`); only the domain's chain order lives here.
export function drillUp(granularity: OverviewGranularity): OverviewGranularity {
  return drillStep(DRILL_ORDER, granularity, "up");
}

export function drillDown(granularity: OverviewGranularity): OverviewGranularity {
  return drillStep(DRILL_ORDER, granularity, "down");
}

// ---------------------------------------------------------------------------
// Response → points
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * ALL period math runs in UTC on the API's `…T00:00:00.000` stamps: local
 * getters would shift the calendar day in negative-offset timezones. Every
 * period string normalizes to the same `…Z` shape, so map lookups (last-year
 * pairing, week buckets, selection) match exactly.
 */

/** `Date` → canonical UTC-midnight ISO period key. */
function iso(date: Date): string {
  return date.toISOString();
}

/** UTC-midnight `Date` from calendar fields (no local-timezone drift). */
function utc(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m, d));
}

/**
 * Maps one API response page into points. The time dimension's field name
 * is DYNAMIC (`Dbo520jh.K005.<granularity>` — types.ts) and every measure
 * arrives as a string, so both are resolved here once. Rows come back
 * sorted oldest → newest (the x-axis order).
 */
export function mapResponseToPoints(
  rows: PrintOverviewResponseRow[],
  granularity: ApiGranularity,
): PrintOverviewPoint[] {
  return rows
    .map((row) => {
      const raw = row[`Dbo520jh.K005.${granularity}`] ?? row["Dbo520jh.K005"];
      return {
        // A stamp without `Z` is UTC midnight on the API's clock — normalize
        // before parsing so every timezone reads the same calendar day.
        period: new Date(raw.includes("Z") ? raw : `${raw}Z`).toISOString(),
        granularity,
        jobCount: Number(row["Dbo520jh.Total"]),
        printedLength: Number(row["Dbo520jh.ConsumptionSum"]),
        inkUsage: Number(row["Dbo520jh.InkSum"]),
        printTimeSeconds: Number(row["Dbo520jh.TimeSum"]),
      };
    })
    .sort((a, b) => a.period.localeCompare(b.period));
}

// ---------------------------------------------------------------------------
// Week aggregation (week is NOT served by the API — fetched as days)
// ---------------------------------------------------------------------------

/**
 * Week start per locale, date-fns `weekStartsOn` convention (0 = Sunday,
 * 1 = Monday), data from CLDR via `Intl.Locale#getWeekInfo`. en-US → 0,
 * fr-FR → 1; ja-JP also resolves to 0 (CLDR: Japanese calendars start on
 * Sunday). Falls back to 1 (ISO Monday) when the API is unavailable.
 */
export function weekStartsOn(locale: string): 0 | 1 {
  try {
    const weekInfo = (
      new Intl.Locale(locale) as Intl.Locale & {
        getWeekInfo?: () => { firstDay: number };
      }
    ).getWeekInfo?.();
    if (weekInfo) {
      // Intl numbers Monday=1 … Sunday=7 → JS `getDay()` 0/1 convention.
      return weekInfo.firstDay === 7 ? 0 : 1;
    }
  } catch {
    // Malformed locale — fall through to the ISO default below.
  }
  return 1;
}

/** Start of the week containing `date`, per `weekStart` (0 = Sun, 1 = Mon). */
function weekStartOf(date: Date, weekStart: 0 | 1): Date {
  const dayOfWeek = date.getUTCDay(); // 0 = Sunday … 6 = Saturday (UTC)
  const offset = (dayOfWeek - weekStart + 7) % 7;
  return utc(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - offset);
}

/**
 * Sums day-level points into WEEK buckets, weeks starting on the
 * locale's first day (weekStartsOn). A week straddling a month boundary
 * belongs to the week containing its days — bucketed purely by week start.
 * Output points carry `granularity: "week"` and the week start as `period`.
 */
export function buildWeekBuckets(
  days: PrintOverviewPoint[],
  weekStart: 0 | 1,
): PrintOverviewPoint[] {
  const buckets = new Map<string, PrintOverviewPoint>();
  for (const day of days) {
    const start = weekStartOf(new Date(day.period), weekStart);
    const key = iso(start);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.jobCount += day.jobCount;
      bucket.printedLength += day.printedLength;
      bucket.inkUsage += day.inkUsage;
      bucket.printTimeSeconds += day.printTimeSeconds;
    } else {
      buckets.set(key, {
        period: key,
        granularity: "week",
        jobCount: day.jobCount,
        printedLength: day.printedLength,
        inkUsage: day.inkUsage,
        printTimeSeconds: day.printTimeSeconds,
      });
    }
  }
  return [...buckets.values()].sort((a, b) => a.period.localeCompare(b.period));
}

/**
 * The points a view displays: API points pass through; the `week` level
 * re-buckets the day-level points client-side (no extra fetch).
 */
export function buildDisplayPoints(
  points: PrintOverviewPoint[],
  granularity: OverviewGranularity,
  weekStart: 0 | 1,
): PrintOverviewPoint[] {
  if (granularity !== "week") {
    return points.filter((point) => point.granularity === granularity);
  }
  return buildWeekBuckets(
    points.filter((point) => point.granularity === "day"),
    weekStart,
  );
}

// ---------------------------------------------------------------------------
// Chart dataset: current vs. last-year pairing
// ---------------------------------------------------------------------------

/** One x-axis row: the period's values + the SAME period one year before. */
export interface OverviewChartRow {
  period: string;
  printedLength: number;
  printedLengthLastYear: number | null;
  jobCount: number;
  jobCountLastYear: number | null;
  inkUsage: number;
  inkUsageLastYear: number | null;
}

/**
 * ISO of the paired period one year back. Weeks pair by WEEK ORDINAL
 * within the year (week N of year Y ↔ week N of year Y-1, aligned to the
 * same week start) — a fixed "-52 weeks" shift drifts across year
 * boundaries (53-week years); days/months/years shift the calendar field.
 */
function lastYearPeriodIso(
  period: string,
  granularity: OverviewGranularity,
  weekStart: 0 | 1,
): string | null {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  if (granularity === "day") {
    return iso(utc(y - 1, m, d));
  }
  if (granularity === "month") {
    return iso(utc(y - 1, m, 1));
  }
  if (granularity === "year") {
    return iso(utc(y - 1, 0, 1));
  }
  // Week: ordinal within the year (week 1 = the week containing Jan 1),
  // then the same ordinal in the previous year.
  const yearStart = weekStartOf(utc(y, 0, 1), weekStart);
  const ordinal = Math.round((date.getTime() - yearStart.getTime()) / WEEK_MS);
  const prevYearStart = weekStartOf(utc(y - 1, 0, 1), weekStart);
  return iso(new Date(prevYearStart.getTime() + ordinal * WEEK_MS));
}

/**
 * Dataset rows for BOTH charts: the x-axis is the points' own timeline; for
 * each period the "last year" fields are looked up internally from the SAME
 * response (period − 1 year — the API carries no separate last-year
 * measure). Periods without a last-year match get `null` (no bar/point
 * drawn) — the earliest year of the range never invents data.
 */
export function buildOverviewChartRows(
  points: PrintOverviewPoint[],
  weekStart: 0 | 1,
  locale: string,
): OverviewChartRow[] {
  const byPeriod = new Map(points.map((point) => [point.period, point]));
  return points.map((point) => {
    const lastYearIso = lastYearPeriodIso(point.period, point.granularity, weekStart);
    const lastYear = lastYearIso ? byPeriod.get(lastYearIso) : undefined;
    return {
      period: formatPeriodLabel(point.period, point.granularity, locale, weekStart),
      printedLength: point.printedLength,
      printedLengthLastYear: lastYear?.printedLength ?? null,
      jobCount: point.jobCount,
      jobCountLastYear: lastYear?.jobCount ?? null,
      inkUsage: point.inkUsage,
      inkUsageLastYear: lastYear?.inkUsage ?? null,
    };
  });
}

/** Chart dataset for the EChart wrapper — one row per period, 6 fields. */
export function buildOverviewDataset(rows: OverviewChartRow[]): EChartDataset {
  return {
    dimensions: [
      "period",
      "printedLengthLastYear",
      "printedLength",
      "jobCountLastYear",
      "jobCount",
      "inkUsageLastYear",
      "inkUsage",
    ],
    source: rows,
  };
}

// ---------------------------------------------------------------------------
// Axis labels & bounds
// ---------------------------------------------------------------------------

/**
 * x-axis label per granularity (all UTC — never local-shifted):
 *
 * - **day**: `DD/MM/YYYY` + the week ordinal WITHIN THE YEAR
 *   — "05/01/2026 W2";
 * - **week**: `MM/YYYY` + the same week ordinal — "01/2026 W2";
 * - **month**: numeric `MM/yyyy`; **year**: `yyyy` (compact axis labels).
 *
 * Both W numbers are aligned to the locale's first weekday (`weekStart`:
 * week 1 = the week containing Jan 1), so en-US/ja-JP (Sunday-first) and
 * fr-FR (Monday-first) may disagree at year boundaries by design.
 */
export function formatPeriodLabel(
  period: string,
  granularity: OverviewGranularity,
  _locale: string,
  weekStart: 0 | 1 = 1,
): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  if (granularity === "year") return String(date.getUTCFullYear());
  if (granularity === "day" || granularity === "week") {
    const yearStart = weekStartOf(utc(date.getUTCFullYear(), 0, 1), weekStart);
    const week = Math.round((date.getTime() - yearStart.getTime()) / WEEK_MS) + 1;
    const dayMonthYear = `${pad(date.getUTCDate())}/${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
    return granularity === "day"
      ? `${dayMonthYear} W${week}`
      : `${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()} W${week}`;
  }
  return `${pad(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
}

/** Data-driven secondary-axis bound (peak → "nice" maximum) — no clipping. */
export function secondaryAxisMax(values: number[]): number {
  return niceMax(Math.max(...values, 0));
}

/**
 * Client-side sort of the chart points (chart-sort plan): applied BEFORE
 * the dataset rows are built AND before the selection indices are looked
 * up, so the bars, the last-year pairing (keyed by period, order-blind)
 * and the highlight always read the SAME order. `period` compares the ISO
 * stamps — the DISPLAYED label (`01/2026 W5`) must never be string-sorted.
 *
 * The `*LastYear` fields sort by the PAIRED last-year point's value (same
 * `lastYearPeriodIso` rule as the dataset rows — week pairs by ordinal).
 * Periods WITHOUT a last-year match (the earliest year of the range) go to
 * the END in BOTH directions — "no data" is not a small value, and keeping
 * them out of the front means an ascending sort visibly reorders the chart
 * immediately (nulls-first would leave the opening window looking exactly
 * like the chronological order).
 */
export function sortOverviewPoints(
  points: PrintOverviewPoint[],
  sort: ChartSort,
  weekStart: 0 | 1 = 1,
): PrintOverviewPoint[] {
  const byLastYear = sort.id.endsWith("LastYear")
    ? (sort.id.slice(0, -"LastYear".length) as "printedLength" | "jobCount" | "inkUsage")
    : null;
  if (!byLastYear) {
    return applyChartSort(points, sort, {
      period: (a, b) => a.period.localeCompare(b.period),
    });
  }
  // Last-year sort: partition unmatched periods out FIRST (stable), sort
  // only the matched ones, then append the unmatched at the end.
  const byPeriod = new Map(points.map((point) => [point.period, point]));
  const lastYearValue = (point: PrintOverviewPoint): number | null => {
    const lastYearIso = lastYearPeriodIso(point.period, point.granularity, weekStart);
    return (lastYearIso ? byPeriod.get(lastYearIso) : undefined)?.[byLastYear] ?? null;
  };
  const matched: PrintOverviewPoint[] = [];
  const unmatched: PrintOverviewPoint[] = [];
  for (const point of points) {
    (lastYearValue(point) === null ? unmatched : matched).push(point);
  }
  return [
    ...applyChartSort(matched, sort, {
      [sort.id]: (a: PrintOverviewPoint, b: PrintOverviewPoint) =>
        (lastYearValue(a) as number) - (lastYearValue(b) as number),
    }),
    ...unmatched,
  ];
}

// ---------------------------------------------------------------------------
// KPI aggregation (totals live in Redux via the widget's points; the KPI
// numbers are DERIVED selectors — never a second copy of the data)
// ---------------------------------------------------------------------------

/** Half-open span [start, end) of one point's own granularity. */
function pointSpan(point: PrintOverviewPoint): { start: number; end: number } {
  const start = new Date(point.period).getTime();
  if (point.granularity === "day") return { start, end: start + DAY_MS };
  if (point.granularity === "week") return { start, end: start + WEEK_MS };
  if (point.granularity === "month") {
    const date = new Date(point.period);
    const end = utc(date.getUTCFullYear(), date.getUTCMonth() + 1, 1).getTime();
    return { start, end };
  }
  const date = new Date(point.period);
  const end = utc(date.getUTCFullYear() + 1, 0, 1).getTime();
  return { start, end };
}

/** Half-open span of a SELECTION period (a "week" selection spans 7 days). */
function selectionSpan(selection: NonNullable<OverviewSelection>): { start: number; end: number } {
  const start = new Date(selection.period).getTime();
  if (selection.granularity === "day") return { start, end: start + DAY_MS };
  if (selection.granularity === "week") return { start, end: start + WEEK_MS };
  if (selection.granularity === "month") {
    const date = new Date(selection.period);
    const end = utc(date.getUTCFullYear(), date.getUTCMonth() + 1, 1).getTime();
    return { start, end };
  }
  const date = new Date(selection.period);
  const end = utc(date.getUTCFullYear() + 1, 0, 1).getTime();
  return { start, end };
}

function sumPoints(points: PrintOverviewPoint[]): OverviewKpiTotals {
  return points.reduce(
    (totals, point) => ({
      jobCount: totals.jobCount + point.jobCount,
      printedLength: totals.printedLength + point.printedLength,
      inkUsage: totals.inkUsage + point.inkUsage,
      printTimeSeconds: totals.printTimeSeconds + point.printTimeSeconds,
    }),
    { jobCount: 0, printedLength: 0, inkUsage: 0, printTimeSeconds: 0 },
  );
}

/**
 * A day row counts as a RUNNING day only when it carries activity. The API
 * returns rows for days WITH data, but if a backend ever emits an all-zero
 * row (day present in the source table, machine idle) the guard keeps the
 * active-days denominator exact — totals are unaffected either way.
 */
function isRunningDay(point: PrintOverviewPoint): boolean {
  return (
    point.jobCount > 0 ||
    point.printedLength > 0 ||
    point.inkUsage > 0 ||
    point.printTimeSeconds > 0
  );
}

/**
 * The four KPI figures, computed from DAY-LEVEL points ONLY (the caller
 * feeds the day cache — `selectPrintOverviewKpiBase`): a day row exists
 * ONLY when that day actually ran, so:
 *
 * - totals sum the day rows in scope;
 * - the Average (Per Day) denominator is the count of RUNNING days
 *   ("active days", all-zero rows excluded) — NOT the calendar span, which
 *   would dilute the averages with idle weekends/off days;
 * - with a selection → the same math over the days inside the selected
 *   period (day/week/month/year span — the cross-chart sync target).
 *
 * Day rows also make the denominator backend-exact: no separate
 * "count active days" API is needed — grouping by date already returns
 * one row per day WITH data.
 */
export function computeKpi(
  points: PrintOverviewPoint[],
  selection: OverviewSelection,
): OverviewKpi {
  const inScope = selection
    ? points.filter((point) => {
        const span = pointSpan(point);
        const range = selectionSpan(selection);
        return span.start < range.end && range.start < span.end;
      })
    : points;
  const scoped = inScope.filter(isRunningDay);

  const totals = sumPoints(scoped);
  const activeDays = Math.max(scoped.length, 1);

  return {
    ...totals,
    avgPerDay: {
      jobCount: totals.jobCount / activeDays,
      printedLength: totals.printedLength / activeDays,
      inkUsage: totals.inkUsage / activeDays,
      printTimeSeconds: totals.printTimeSeconds / activeDays,
    },
  };
}

/**
 * Indices of the points overlapping the selection — the columns a chart
 * highlights. Cross-granularity by design: a MONTH selection lights up the
 * matching month column in a month chart, ALL its day columns in a day
 * chart, the overlapping weeks in a week chart, and the year column in a
 * year chart.
 */
export function selectedPeriodIndices(
  points: PrintOverviewPoint[],
  selection: OverviewSelection,
): number[] {
  if (!selection) return [];
  const range = selectionSpan(selection);
  return points
    .map((point, index) => {
      const span = pointSpan(point);
      return span.start < range.end && range.start < span.end ? index : -1;
    })
    .filter((index) => index >= 0);
}

// ---------------------------------------------------------------------------
// Selection lookup (click → period id)
// ---------------------------------------------------------------------------

/** The selection id a clicked column represents (stable business id). */
export function periodAt(points: PrintOverviewPoint[], index: number): string | null {
  return points[index]?.period ?? null;
}

/** Seconds → `h:mm:ss` (hours exceed 999 freely: 15383:51:30). */
export function formatDuration(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) ? Math.round(totalSeconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return `${hours}:${pad(minutes)}:${pad(seconds)}`;
}
